import {type FC, useMemo} from 'react';

import {useTheme} from 'styled-components';

import type {Text} from '../../uikit/Text';
import type {
  GetComponentProps,
  WithDomStyleProp,
} from '../../utils/utility-types';
import type {Props} from './types';

import {getColor} from '../../styles';
import {getRandomColor} from '../../styles/utils/getRandomColor';
import {getCardBrandImage} from '../../utils/entity/card/getCardBrandImage';
import * as S from './styles';

const dotGroups = new Array(3).fill(undefined);

const CARD_COLORS = [
  '#6D6DFB',
  '#38A3A5',
  '#355070',
  '#A87DE2',
  '#008000',
  '#FFA500',
];

export const CreditCard: FC<WithDomStyleProp<Props>> = ({
  accentColor,
  brand,
  colorIndex = -1,
  expMonth,
  expYear,
  last4Digits,
  variant,
  ...rest
}) => {
  const theme = useTheme();

  const brandImage = useMemo(() => getCardBrandImage(brand, true), [brand]);

  const backgroundColor = useMemo(() => {
    if (accentColor) {
      return getColor(accentColor)({theme});
    }

    if (colorIndex >= 0) {
      return CARD_COLORS[colorIndex % CARD_COLORS.length]!;
    }

    // you can add a random string to the end of the seed to get a different color
    const seed = `${brand}_${last4Digits}_${expMonth}_${expYear}_exam`;

    return getRandomColor(seed);
  }, [brand, colorIndex, expMonth, expYear, last4Digits, accentColor, theme]);

  const renderDots = () =>
    !variant ? (
      <>
        {dotGroups.map((_, i) => (
          <S.DotsGroup key={i}>
            <S.Dot />
            <S.Dot />
            <S.Dot />
            <S.Dot />
          </S.DotsGroup>
        ))}
      </>
    ) : null;

  const renderExpDate = () =>
    !variant ? <S.Text>{`${expMonth}/${expYear}`}</S.Text> : null;

  const renderBrandImage = () =>
    brandImage ? <S.BrandImage src={brandImage} variant={variant} /> : null;

  const textVariant: GetComponentProps<typeof Text>['variant'] =
    variant?.includes('small') ? 'label.la' : 'body';

  if (variant === 'smallv2') {
    return (
      <S.Container
        {...rest}
        $backgroundColor={backgroundColor}
        variant={variant}
      >
        <S.CardMediumBackground />
        {renderBrandImage()}
        <S.NumberContainer variant={variant}>
          <S.CardInfoText>{last4Digits}</S.CardInfoText>
        </S.NumberContainer>
      </S.Container>
    );
  }

  return (
    <S.Container {...rest} $backgroundColor={backgroundColor} variant={variant}>
      <S.NumberContainer variant={variant}>
        {renderDots()}
        <S.Text variant={textVariant}>{`${last4Digits}`}</S.Text>
      </S.NumberContainer>
      {renderExpDate()}
      {renderBrandImage()}
    </S.Container>
  );
};
